import { useEffect, useMemo, useState } from 'react';
import { ScrollText, ChevronDown, ChevronRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AdminAPI } from '@/lib/admin-api';
import { formatDateTime } from '@/lib/format-time';
import { cn } from '@/lib/utils';

const ACTION_BADGE = {
  create: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  update: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  delete: 'bg-rose-500/10 text-rose-600 border-rose-500/30',
  publish: 'bg-primary/10 text-primary border-primary/30',
  archive: 'bg-slate-500/10 text-slate-600 border-slate-500/30',
  rollback: 'bg-accent/10 text-accent border-accent/30',
};

export default function AdminAuditLog() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [entityType, setEntityType] = useState('all');
  const [action, setAction] = useState('all');
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    setLoading(true);
    AdminAPI.auditLogs({
      entity_type: entityType !== 'all' ? entityType : undefined,
      action: action !== 'all' ? action : undefined,
      limit: 200,
    })
      .then(setEvents)
      .finally(() => setLoading(false));
  }, [entityType, action]);

  const filtered = useMemo(() => {
    if (!q) return events;
    const qq = q.toLowerCase();
    return events.filter((e) =>
      (e.actor_email || '').toLowerCase().includes(qq) ||
      (e.entity_id || '').toLowerCase().includes(qq) ||
      (e.diff_summary || '').toLowerCase().includes(qq)
    );
  }, [events, q]);

  // Group by day
  const grouped = useMemo(() => {
    const map = {};
    for (const e of filtered) {
      const day = (e.timestamp || '').slice(0, 10);
      (map[day] = map[day] || []).push(e);
    }
    return Object.entries(map).sort(([a], [b]) => (a < b ? 1 : -1));
  }, [filtered]);

  return (
    <div className="space-y-4" data-testid="admin-audit-log-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-accent font-medium">Operations</div>
        <h1 className="mt-1.5 font-display text-xl sm:text-2xl font-semibold tracking-tight">Audit Log</h1>
        <p className="mt-1 text-xs text-muted-foreground">All admin changes — who, what, when. Last 200 events.</p>
      </div>

      <Card className="p-3 flex flex-col sm:flex-row gap-3">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by actor email or entity id..." className="h-9 border-transparent bg-[hsl(var(--surface-2))] flex-1" data-testid="admin-audit-search-input" />
        <div className="grid grid-cols-2 sm:flex gap-2">
          <Select value={entityType} onValueChange={setEntityType}>
            <SelectTrigger className="h-9 w-full sm:w-[160px]" data-testid="admin-audit-filter-entity"><SelectValue placeholder="Entity type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entity types</SelectItem>
              <SelectItem value="bank">Banks</SelectItem>
              <SelectItem value="card">Cards</SelectItem>
              <SelectItem value="rule_version">Rule versions</SelectItem>
            </SelectContent>
          </Select>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="h-9 w-full sm:w-[160px]" data-testid="admin-audit-filter-action"><SelectValue placeholder="Action" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {Object.keys(ACTION_BADGE).map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground" data-testid="admin-audit-empty-state">
          <ScrollText className="h-6 w-6 mx-auto opacity-50 mb-2" />
          <div className="text-sm">No audit events match. Edits will appear here.</div>
        </Card>
      ) : (
        <div className="space-y-5">
          {grouped.map(([day, items]) => (
            <div key={day}>
              <div className="text-[11px] uppercase tracking-[0.16em] text-muted-foreground mb-2 sticky top-12 z-10 bg-[hsl(var(--background))]/85 backdrop-blur py-1">
                {day}
              </div>
              <Card className="divide-y divide-border/40">
                {items.map((e) => {
                  const open = !!expanded[e.id];
                  return (
                    <div key={e.id} className="px-3 py-2.5" data-testid={`admin-audit-event-row-${e.id}`}>
                      <button onClick={() => setExpanded((x) => ({ ...x, [e.id]: !open }))} className="w-full flex items-center gap-3 text-left group" data-testid={`admin-audit-event-toggle-${e.id}`}>
                        <div className="h-2 w-2 rounded-full bg-primary/70 shrink-0 mt-1" />
                        <Badge variant="outline" className={cn('text-[10px] uppercase tracking-wide px-1.5', ACTION_BADGE[e.action] || '')}>{e.action}</Badge>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm truncate">
                            <span className="text-muted-foreground">{e.entity_type}</span>{' '}
                            <span className="font-mono-num text-xs">{e.entity_id}</span>{' '}
                            <span className="text-muted-foreground">—</span> {e.diff_summary || ''}
                          </div>
                          <div className="text-[10px] text-muted-foreground">
                            {e.actor_email || 'system'} · {formatDateTime(e.timestamp)}
                          </div>
                        </div>
                        {open ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                      </button>
                      {open && (
                        <div className="mt-2 ml-7 grid sm:grid-cols-2 gap-2 text-[11px]" data-testid={`admin-audit-event-diff-${e.id}`}>
                          <div className="rounded-md border border-border bg-[hsl(var(--surface-2))] p-2">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Before</div>
                            <pre className="whitespace-pre-wrap break-all font-mono-num leading-relaxed text-[11px] max-h-60 overflow-auto">
                              {e.before ? JSON.stringify(e.before, null, 2) : '—'}
                            </pre>
                          </div>
                          <div className="rounded-md border border-border bg-[hsl(var(--surface-2))] p-2">
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">After</div>
                            <pre className="whitespace-pre-wrap break-all font-mono-num leading-relaxed text-[11px] max-h-60 overflow-auto">
                              {e.after ? JSON.stringify(e.after, null, 2) : '—'}
                            </pre>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </Card>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
